import React, {ChangeEventHandler, useCallback, useState} from 'react';

import debounce from 'lodash.debounce';
import {searchAdminUsers} from 'src/openapi/admin/apis/SearchAdminUsersApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';


import {AdminSuggestResult} from './admin-suggest-result';
import {selectUserByEmail, shouldAddToMembers, splitQuery} from './utils';

import type {ClipboardEvent} from 'react';
import type {AdminSearchInfo} from 'src/openapi/admin/models/AdminSearchInfo';
import type {ServerResponse} from 'src/typedef';

type Props = {
    id: string,
    profileId: number,
    addedMembers: Array<AdminSearchInfo>,
    onAdd: (admin: AdminSearchInfo) => void,
    onBulkAdd: (admins: Array<AdminSearchInfo>) => void,
};

const SEARCH_TIMEOUT = 1000;
const MAX_RESULTS = 30;

export const AdminSuggest: React.FC<Props> = ({id, profileId, onAdd, onBulkAdd, addedMembers}) => {
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();

    const [searchedQuery, setQuery] = useState<string>('');
    const [response, setResponse] = useState<ServerResponse<AdminSearchInfo[]> | null>(null);

    const [busy, setBusy] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadOneUser = useCallback(debounce(async (newValue: string) => {
        const signal = createAbortSignal();
        const email = newValue.trim();
        if (email) {
            const apiResponse = await apiDispatch(searchAdminUsers(
                {email, profileId, excludeExisting: false},
                {signal},
            ));

            if (apiResponse.payload.success) {
                setResponse({data: apiResponse.payload.data
                    .filter(({userInfo}) => !addedMembers.map(e => e.userInfo.email).includes(userInfo.email))
                    .slice(0, MAX_RESULTS),
                });
            } else {
                setResponse({error: {message: 'Can\'t suggest users. Service is temporary unavailable.'}});
            }
            setBusy(false);
        } else {
            abort();
            setResponse(null);
            setBusy(false);
        }
    }, SEARCH_TIMEOUT), [profileId, addedMembers, apiDispatch]);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        loadOneUser(value);
    }, [setBusy, setQuery, loadOneUser]);

    const bulkLoad = useCallback((e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text');
        const emails = splitQuery(text);

        if (!emails.length) {
            return;
        }

        setBusy(true);

        const bulkAddedMembers = emails.reduce((acc, email) => {
            return [...acc, apiDispatch(searchAdminUsers({profileId, email, excludeExisting: false})).then(resp => {
                if (resp.payload.success) {
                    return selectUserByEmail({data: resp.payload.data, searchByEmail: email});
                }
                return undefined;
            })];
        }, [] as Array<Promise<AdminSearchInfo | undefined>>);

        Promise.all(bulkAddedMembers).then(newMembers => {
            setBusy(false);
            setQuery('');
            const users = newMembers
                .reduce((acc, item) => {
                    if (item && shouldAddToMembers(addedMembers, item, profileId)) {
                        return [...acc, item];
                    }
                    return acc;
                }, [] as Array<AdminSearchInfo>);

            if (users.length) {
                onBulkAdd(users);
            }
        });

    }, [profileId, addedMembers, apiDispatch, onBulkAdd]);

    const clearSuggest = useCallback(() => {
        setResponse(null);
        setQuery('');
    }, [setResponse, setQuery]);

    const onItemSelect = useCallback((data: AdminSearchInfo) => {
        clearSuggest();
        onAdd(data);
    }, [onAdd, clearSuggest]);

    return (
        <div onPaste={bulkLoad}>
            <Autosuggest
                id={id}
                value={searchedQuery}
                onChange={onChangeQuery}
                placeholder="Enter or paste email addresses"
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <AdminSuggestResult id={id} busy={busy} response={response}/>
            </Autosuggest>
        </div>
    );
};
